import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { accessLevelToHeaders } from '@app/core/utilities/api.utilities';
import { environment } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiAccessLevel, ApiCommonRoutes, ApiEndpoint, ApiRequestHeader } from '../models/api.models';
import { Account, Campaign, CustomerSummary } from '../models/common.models';
import { CurrencyRates } from '../models/customer.models';
import { LoggingService } from '../services/error-logging.service';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class CommonAPI {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private logger: LoggingService,
  ) {}

  updateUserInfo(userID: number, modifiedUserInfo: {}): Observable<boolean> {
    return this.http.patch(`${environment.app.resources.dashboard}users/${userID}`, modifiedUserInfo).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Updating user details failed', error, 'error');
        return throwError(() => 'Updating user details failed');
      }),
    );
  }

  getCurrencyRate(currencies): Observable<CurrencyRates> {
    return this.apiService
      .getList({
        limit: 100,
        endpoint: ApiEndpoint.CurrencyRates,
        filterValues: <any>{ currency: [...new Set(currencies)] },
      })
      .pipe(
        map(({ data }: any) => {
          return data.reduce((acc, d) => {
            data.forEach((e) => (acc[`${e.currency}-${d.currency}`] = d.rateFromEur / e.rateFromEur));

            return acc;
          }, {}) as CurrencyRates;
        }),
      );
  }

  getAccountById(adspertId: string): Observable<Account> {
    return this.apiService
      .getObject({ endpoint: ApiEndpoint.Accounts, route: [adspertId] })
      .pipe(map(({ data }) => accountDTO(data)));
  }

  getAccounts(refiners): Observable<Account[]> {
    return this.apiService
      .getList({ endpoint: ApiEndpoint.Accounts, ...refiners })
      .pipe(map(({ data }) => data.map(accountDTO)));
  }

  getCampaignById(customerId: number, campaignId: string) {
    return this.apiService
      .getObject<Campaign>({
        endpoint: ApiEndpoint.Customers,
        route: [customerId, ApiCommonRoutes.Campaigns, campaignId],
      })
      .pipe(
        map(({ data }) => {
          return {
            name: data.campaignName,
            id: data.campaignId,
            pgId: data.performanceGroupId,
            status: data.campaignStatus,
            optimizationSupport: data.campaignOptimizationSupport,
            tags: { user: data.accountTagsUser, system: [] },
          };
        }),
      );
  }

  makeRequest(
    url: string,
    query: string,
    accessLevel?: ApiAccessLevel,
  ): Observable<{ data: any[] | any; total: number }> {
    return this.http
      .get(`${environment.app.resources.dashboard}${url}?${query}`, { headers: accessLevelToHeaders(accessLevel) })
      .pipe(
        map((response: any) => {
          return {
            data: response.data,
            total: response.meta.pagination?.total,
          };
        }),
        catchError((error: any) => {
          this.logger.logError('Could not fetch data for: ' + url, error, 'error');
          return throwError(() => 'Could not fetch data for: ' + url);
        }),
      );
  }
  getDefaultAccount(id: string, customerId = '0'): Observable<any> {
    const refiner = { limit: 1, sortValues: { connectedAt: -1 } };
    if (!!customerId) refiner['filterValues'] = { customerId };

    const sub$ = !!id ? this.getAccountById(id) : this.getAccounts(refiner).pipe(map((d) => d?.[0]));
    return sub$.pipe(map((d) => (!!d ? d : null)));
  }
  export(uri: string, fields: any, columnNames: any, type: 'csv' | 'xls') {
    const headers: any = {
      [ApiRequestHeader.TableColumns]: encodeURIComponent(columnNames.join(',')), // escape()
    };
    if (type === 'xls') {
      headers.Accept = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    } else {
      headers.Accept = 'text/csv';
      headers[ApiRequestHeader.Humanize] = 'true';
    }
    return this.http
      .get(`${uri}&include=${fields.join(',')}`, {
        responseType: 'arraybuffer',
        headers,
      })
      .pipe(
        map((response: any) => {
          let filename = '';
          const disposition = response.headers.get('content-disposition');
          if (disposition && disposition.indexOf('attachment') !== -1) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
            if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
          }
          delete response.headers;

          const blob = new Blob([response], { type: headers.Accept });
          const a: any = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          document.body.appendChild(a);

          a.style = 'display: none';
          a.href = url;
          a.download = filename;
          a.taget = '_blank';
          a.click();
          document.body.removeChild(a);
        }),
        catchError((error: any) => throwError(() => error)),
      );
  }
  getInvoiceNumber(key: string): Observable<any> {
    return this.http.get(`/billed-usage-report/${key}/details`).pipe(
      map((res: any) => {
        return res.data.invoice_number;
      }),
      catchError((error: any) => {
        if (error.code === 404) {
          return throwError(() => 404);
        } else {
          this.logger.logError('Could not fetch invoice information', error, 'error');
          return throwError(() => 'Could not fetch invoice information');
        }
      }),
    );
  }
  getCustomerSummary(customerId, refiners): Observable<CustomerSummary> {
    const keysToRemove = ['campaignAmzRasCampaign', 'campaignsCountLe', 'campaignsCountGe'];

    for (const key of keysToRemove) {
      if (key in refiners?.filterValues) {
        delete refiners.filterValues[key];
      }
    }

    return this.apiService
      .getObject<CustomerSummary>({
        endpoint: ApiEndpoint.Customers,
        route: [customerId, ApiCommonRoutes.Summary],
        ...refiners,
      })
      .pipe(
        map(({ data }) => {
          data.campaignOptimizationTypes = data.campaignOptimizationTypes.sort().reverse();
          return data;
        }),
      );
  }
}

const accountDTO = (data) => {
  data.optimizationStart = data.optimizationStart ? null : new Date(data.optimizationStart);
  data.connectedAt = data.connectedAt ? null : new Date(data.connectedAt);
  data.tags = { user: data.tagsUser, system: data.tagsSystem };
  data.amzHasMWS = !!data.amzHasMwsAccess;

  return <Account>data;
};
